import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import {
  handleEmailLogin,
  handleGoogleLogin,
  google_sign_client_id,
  isAuthenticated,
} from '../../apiservices/axiosservice/authentication';
import './styles/LoginScreen.css';
import { useNavigate } from 'react-router-dom';
import Loading from './components/Loading';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setUserPermissions } from '../../redux/slices/access/permissionSlice';
import { fetchCountry } from '../../apiservices/axiosservice/gameservice';

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailUsername, setEmailUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [countryName, setCountryName] = useState<string>('');
  const dispatch = useDispatch();

  const setUserAuthentication = (data) => {
    dispatch(
      setUserPermissions({
        privileges: data.permissions,
        isAuthenticated: isAuthenticated(),
        isRehydrating: false,
      })
    );
  };
  const handleLoginFailure = (error) => {};
  const handleLoginSuccess = async (response) => {
    try {
      setIsLoading(true);
      const serverResponse = await handleGoogleLogin({
        token: response.credential,
        countryName,
      });
      setIsLoading(false);
      if (serverResponse.data) {
        setUserAuthentication(serverResponse.data);

        const { loginMode, userId, verified, profiled } =
          serverResponse.data.user;
        if (loginMode === 'google') {
          if (profiled) {
            // go to dashboard
            navigate('/dashboard/home');
          } else {
            // Go to profile page
            navigate('/profile-subscriber');
          }
        }
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setErrorMessage(
        error.message || 'Google authentication failed. Please try again.'
      );
    }
  };
  const handleCustomLogin = async () => {
    try {
      const requestData = {
        email: emailUsername,
        password: password,
        countryName: countryName,
      };
      setIsLoading(true);
      const response = await handleEmailLogin(requestData);
      // navigate('/login-verify');
      setIsLoading(false);
      if (response.data) {
        setIsLoading(false);
        setUserAuthentication(response.data);
        const { loginMode, userId, verified, profiled } = response.data.user;
        if (loginMode === 'custom') {
          if (!verified) {
            // go to confirmation page
            setErrorMessage(
              'Please check your email inbox and verify the email'
            );
          }
          if (verified && profiled) {
            // go to dashboard
            navigate('/dashboard/home');
          }
          if (verified && !profiled) {
            // Go to profile page
            navigate('/profile-subscriber');
          }
        }
      }
    } catch (error) {
      setErrorMessage(error.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCountry()
      .then((country) => setCountryName(country))
      .catch(() => setCountryName(''));
  }, []);

  return (
    <>
      <div className="login-panel-container">
        {/* Left panel */}
        <div className="login-left">
          <button className="login-back-btn" onClick={() => navigate(-1)}>
            ‹ Back
          </button>
          <img src="/funhives.png" alt="FunHives" className="login-logo" />
          <p className="login-tagline">JOIN MILLIONS OF QUIZ CREATORS</p>
          <div className="login-features">
            <div className="login-feature-row">
              <span className="login-feature-icon login-feature-icon--blue">
                ⭐
              </span>
              <div>
                <strong>Smart Quiz</strong>
                <p>AI-powered generation</p>
              </div>
            </div>
            <div className="login-feature-row">
              <span className="login-feature-icon login-feature-icon--orange">
                ◎
              </span>
              <div>
                <strong>Geo Guess</strong>
                <p>Location challenges</p>
              </div>
            </div>

            <div className="login-feature-row">
              <span className="login-feature-icon login-feature-icon--purple">
                ✿
              </span>
              <div>
                <strong>Sketcher</strong>
                <p>Object tracing &amp; art</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div className="login-right">
          <div className="login-form-wrapper">
            <h1 className="login-heading">Login to your account</h1>
            <p className="login-subheading">
              Start building quizzes in seconds
            </p>

            <div className="login-field">
              <input
                type="email"
                className="login-input login-input--with-icon-right"
                placeholder="Email Address"
                value={emailUsername}
                onChange={(e) => setEmailUsername(e.target.value)}
                maxLength={60}
              />
              <span className="login-field-icon-right">✉</span>
            </div>
            <div className="login-field">
              <input
                type={showPassword ? 'text' : 'password'}
                className="login-input login-input--with-icon-right"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                maxLength={20}
              />
              <button
                type="button"
                className="login-field-icon-right"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>

            <button
              className="login-submit-btn"
              onClick={handleCustomLogin}
              disabled={isLoading}
            >
              Login
            </button>

            <div className="login-divider">
              <span>or continue with</span>
            </div>

            <GoogleOAuthProvider clientId={google_sign_client_id}>
              <div className="login-google-wrapper">
                <GoogleLogin
                  onSuccess={handleLoginSuccess}
                  onError={handleLoginFailure}
                  width="100%"
                />
              </div>
            </GoogleOAuthProvider>

            {errorMessage && (
              <div className="auth-error-message">{errorMessage}</div>
            )}
          </div>
        </div>
      </div>
      {isLoading && <Loading message="Authenticating..." />}
    </>
  );
};

export default LoginScreen;
